import { useCallback, useEffect, useRef, useState } from "react";
import { libMisc } from "../../../../../lib/misc";
import { s } from "../../../../../lib/styler";
import { ThreadMessage } from "../../../../../types";
import MessagingThreadMessageDateCategoryDisplay from "./MessagingThreadMessage/MessagingThreadMessageDateCategoryDisplay";
import { useFlash } from "../../../../../lib/hooks/Flash";
import Icon from "../../../../commons/Icon";
import { atom, useAtom } from "jotai";
import LinkifiedText from "../../../../commons/LinkifiedText";
import { LOADING_DIRECTIVE, serviceSync } from "../../../../../services/sync.service";
import HeightOfWidth from "../../../../commons/HeightOfWidth";
import Loader from "../../../../commons/Loader";
import { marked } from 'marked';

export type MessagingThreadMessageProps = {
    message: ThreadMessage;
    lastMessageTimestamp: number;
    onReportOffsetTop: (getElem: ((() => HTMLDivElement) | null), id: string, display: string) => void;
    searchHighlight?: boolean;
}

export default function MessagingThreadMessage({ message: { id, message, media, mediaBlobUrl, direction, timestamp, failed = false, sending = false }, lastMessageTimestamp, onReportOffsetTop, searchHighlight = false }: MessagingThreadMessageProps) {
    const timeDisplay = libMisc.formatTime(timestamp);
    const isDayBorder = libMisc.isDifferentDay(lastMessageTimestamp, timestamp);
    const dateElemRef = useRef<HTMLDivElement>(null);
    const onReportOffsetTopRef = useRef(onReportOffsetTop);
    onReportOffsetTopRef.current = onReportOffsetTop;
    const sendingAnimationFlashOn = useFlash(1000, sending);

    const [_, setFailedActionMenu] = useAtom(serviceSync.atomMessagingThreadFailedActionMenu);
    const [__, setViewImageMedia] = useAtom(serviceSync.atomMessagingThreadViewImageMedia);

    useEffect(() => {
        onReportOffsetTopRef.current(isDayBorder ? (() => dateElemRef.current!) : null, id, libMisc.getRelativeDate(timestamp));
        return () => {
            onReportOffsetTopRef.current(null, id, "");
        };
    }, [isDayBorder, id, timestamp]);

    const failedMenuClickHandler = useCallback(() => {
        setFailedActionMenu({
            cb: (action) => {
                if (action === "Retry") {
                    serviceSync.sendMessage(message, id);
                }
                else if (action === "Delete") {
                    serviceSync.deleteMessage(id);
                }
            }
        });

    }, []);

    const mediaClickHandler = useCallback(() => {
        setViewImageMedia({ mediaBlobUrl, open: true });
    }, [mediaBlobUrl]);

    const renderMessageContent = () => {
        if (direction === "from") {
            const html = marked.parse(message) as string;
            return <div dangerouslySetInnerHTML={{ __html: html }} />;
        }
        return <LinkifiedText text={message} />;
    };

    return (
        <div>
            {isDayBorder && (
                <MessagingThreadMessageDateCategoryDisplay ref={dateElemRef} display={libMisc.getRelativeDate(timestamp)} />
            )}
            {message === LOADING_DIRECTIVE && (
                <div className="pl-[20px]"><Loader /></div>
            )}
            <div className={s(`flex px-[22px] items-center`, {
                "hidden": message === LOADING_DIRECTIVE
            }, [
                ["justify-start", "justify-end", direction === "from"]
            ])}>
                {failed && (
                    <div className="pl-[5px] flex items-center mt-[0px] py-[5px] cursor-pointer group active:bg-gray-200 md:active:bg-transparent" onClick={failedMenuClickHandler}>
                        <div className="text-red-400 group-hover:text-gray-500 transition-colors duration-200 mr-[-9px]"><Icon name="bx-error-alt" size="30px" /></div>
                        <div className="text-gray-400 group-hover:text-gray-500 transition-colors duration-200"><Icon name="bx-dots-vertical-rounded" size="28px" /></div>
                    </div>
                )}
                <div className={s("px-[19px] py-[15px] rounded-[15px] text-[13px] max-w-[85%] md:max-w-[60%] flex overflow-hidden border", {
                    "bg-[#eeeeee] border-[#eeeeee]": direction === "from" && !sending && !failed && !searchHighlight,
                    "bg-[#86f39e] border-[#86f39e]": direction === "to" && !sending && !failed && !searchHighlight,
                    "bg-blue-200 border-blue-200": !sending && searchHighlight,
                    "border-blue-200 transition-colors duration-1000": sending,
                    "bg-blue-200": sending && !sendingAnimationFlashOn,
                    "bg-gray-100": sending && sendingAnimationFlashOn,
                    "bg-white border-red-300": !sending && failed && !searchHighlight,
                    "bg-blue-200 border-red-300": !sending && failed && searchHighlight,
                    "w-[350px]": !!media && !!mediaBlobUrl
                })}>
                    <div className="w-full">
                        {!media && (
                            <>
                                <div className={s("flex h-full float-right items-end text-[11px] relative top-[7px] left-[2px]", {}, [
                                    ["text-red-500", "text-gray-600", failed]
                                ])} style={{
                                    shapeOutside: "inset(calc(100% - 0px) 0 0)"
                                }}>{timeDisplay}</div>
                                <div>{renderMessageContent()} &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{timeDisplay.length === 6 ? <>&nbsp;&nbsp;</> : ""}</div>
                            </>
                        )}
                        {!!media && !!mediaBlobUrl && [".png", ".jpg", ".jpeg", "webp"].some(x => media.endsWith(x)) && (
                            <>
                                <div className="flex pb-[15px] w-full relative cursor-pointer" onClick={mediaClickHandler}>
                                    <HeightOfWidth ratio={mediaBlobUrl.aspectRatio!}>
                                        <img className="w-full h-full" src={mediaBlobUrl.blobUrl} />
                                    </HeightOfWidth>
                                    <div className="absolute bottom-0 left-0 right-0 flex justify-end">
                                        <div className={s("text-[11px] relative top-[7px]", {}, [
                                            ["text-red-500", "text-gray-600", failed]
                                        ])}>{timeDisplay}</div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}